import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Grid, Stack, Typography } from '@mui/material';
import {
  Breadcrumbs,
  configureFlowQuery,
  DianaFeaturesEnum,
  FlowResourceType,
  PageLayout,
  PageNames,
  useCurrentUserStore,
  useDynamicBackground,
  useEntities,
  useEnvironment,
  useFeatures,
  useLocationTrackingForWorkflows,
  useWorkflows,
  HeaderWithScrollables,
  ActionButton,
  useCreateWorkflowActions,
  useAppStore,
  localStorageUtil,
  LocalStorageKey,
  entitiesQuery,
  flowsQuery,
  useIsOnline,
} from '@diana/core';
import { WorkflowCard } from './WorkflowCard';
import { WorkflowDraftCard } from './WorkflowDraftCard';
import { WorkflowsEmptyView } from './WorkflowsEmptyView';
import { ThingsGroupCard } from '../ThingsGroupsPage/ThingsGroupCard';
import { useBreadcrumbsRoot } from '../../hooks/useBreadcrumbsRoot';
import { isEqual } from 'lodash';
import { WorkflowsGenerateEmptyView } from './WorkflowsGenerateEmptyView';
import { WorkflowSkeletonCard } from './WorkflowSkeletonCard';

export const WorkflowsPage = () => {
  const [isProcessing, setProcessing] = useState(false);

  useLocationTrackingForWorkflows({ trackOnMount: true });

  const isOnline = useIsOnline();
  const features = useFeatures();
  const environmentName = useEnvironment();
  const images = useCurrentUserStore(
    (state) => ({
      lightImage: state.info?.environmentSettings?.lightBackgroundImage,
      darkImage: state.info?.environmentSettings?.darkBackgroundImage,
    }),
    isEqual,
  );
  const isGeneratingWorkflows = useAppStore((store) => store.isGeneratingWorkflows);

  const createWorkflow = useCreateWorkflowActions();

  const { hasImage } = useDynamicBackground(images);

  const onFetchFinished = useCallback(() => setProcessing(false), []);
  const {
    data,
    isLoading: isLoadingFlows,
    isFetched: isFlowsFetched,
    refetch,
  } = useWorkflows({
    onSuccess: onFetchFinished,
    onError: onFetchFinished,
  });

  const hasToShowEntities =
    Boolean(environmentName) && features[DianaFeaturesEnum.ShowAssetsOnWorkPage];

  const {
    data: entities,
    isLoading: isLoadingEntities,
    refetch: refetchEntities,
  } = useEntities({
    onSuccess: onFetchFinished,
    onError: onFetchFinished,
    enabled: hasToShowEntities,
    params: features[DianaFeaturesEnum.ShowAssetsOnWorkPageQueryParams],
  });

  const totalCountQuery = entitiesQuery.assetCount({});

  const { data: countData, isFetching: isFetchingThingsCount } = useQuery({
    queryKey: totalCountQuery.key,
    queryFn: totalCountQuery.fn,
    enabled: hasToShowEntities && entities?.length && Boolean(environmentName),
    staleTime: 0,
  });

  const isInitiallyLoadingFlows = isLoadingFlows && !isFlowsFetched;
  const isLoading = isLoadingFlows || (hasToShowEntities && isLoadingEntities);
  const searchDraftQuery = configureFlowQuery.searchDraft({
    environmentName,
    flowResourceType: FlowResourceType.Work,
  });

  const searchFlowsCountQuery = flowsQuery.searchFlowCount({
    flowType: environmentName,
  });
  const {
    data: draftData,
    isLoading: isLoadingDraftFlows,
    refetch: refetchDraftFlows,
  } = useQuery({
    queryKey: searchDraftQuery.key,
    queryFn: searchDraftQuery.fn,
    enabled: Boolean(environmentName),
    staleTime: 0,
  });

  const { data: flowsCountData, isFetching: isFetchingFlowsCount } = useQuery({
    queryKey: searchFlowsCountQuery.key,
    queryFn: searchFlowsCountQuery.fn,
    enabled: Boolean(environmentName),
    staleTime: 0,
  });

  // ensure flows and drafts to be an array
  const flows = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const draftFlows = useMemo(() => (Array.isArray(draftData) ? draftData : []), [draftData]);

  const isGeneratingOnboardingWorkflows =
    !!localStorageUtil.getItem({ key: LocalStorageKey.isGeneratingFlows }) &&
    isGeneratingWorkflows &&
    flows.length <= 1;

  const breadcrumbRoot = useBreadcrumbsRoot('work');
  const isContentLoading =
    isLoading ||
    isProcessing ||
    (isLoadingDraftFlows && draftData === undefined) ||
    createWorkflow.isCreatingEmptyFlow;

  return (
    <PageLayout
      name={PageNames.WORKFLOWS}
      title={breadcrumbRoot.title}
      showOfflineError={false}
      isContentLoading={
        (isGeneratingWorkflows && !isInitiallyLoadingFlows) || isGeneratingOnboardingWorkflows
          ? false
          : isContentLoading
      }
      headerIsTransparent={hasImage}
      header={
        <HeaderWithScrollables
          left={<Breadcrumbs items={[breadcrumbRoot]} />}
          right={
            !!createWorkflow.actions.length &&
            !isContentLoading &&
            !isGeneratingOnboardingWorkflows &&
            isOnline ? (
              <ActionButton
                actions={createWorkflow.actions}
                menuWidth={218}
                preventUseMonoActionIcon
                preventUseMonoActionTitle
                sx={({ spacing }) => ({ height: spacing(4) })}
              >
                {createWorkflow.displayName}
              </ActionButton>
            ) : null
          }
        />
      }
    >
      {!isLoading && !isLoadingDraftFlows && !flows.length && !draftFlows.length ? (
        isGeneratingWorkflows ? (
          <WorkflowsGenerateEmptyView />
        ) : (
          <WorkflowsEmptyView />
        )
      ) : isGeneratingOnboardingWorkflows ? (
        <WorkflowsGenerateEmptyView />
      ) : (
        <Stack direction="column" spacing={2}>
          {!!flows.length && (
            <Box>
              <Grid container rowSpacing={2} spacing={2}>
                {hasToShowEntities && !isLoadingEntities
                  ? entities
                      ?.filter((e) => e.flow?.status?.toLocaleLowerCase() === 'active')
                      .map((entity) => {
                        const flowOrigin = entity.flow?.flowOriginId;
                        const countAssetData =
                          flowOrigin && countData ? countData[flowOrigin] : null;
                        return (
                          <ThingsGroupCard
                            key={entity.flow?.flowOriginId}
                            entity={entity}
                            totalCount={countAssetData}
                            isFetching={isFetchingThingsCount}
                            refetch={refetchEntities}
                            setProcessing={setProcessing}
                          />
                        );
                      })
                  : null}
                {!isLoadingFlows
                  ? flows
                      .filter((e) => e.status?.toLocaleLowerCase() === 'active')
                      .map((flow) => (
                        <WorkflowCard
                          key={flow.id}
                          flow={flow}
                          refetch={refetch}
                          totalItems={flowsCountData ? flowsCountData[flow?.flowOriginId] : null}
                          isFetching={isFetchingFlowsCount}
                          setProcessing={setProcessing}
                        />
                      ))
                  : null}
                {isGeneratingWorkflows
                  ? new Array(5).fill(null).map((_, index) => {
                      return <WorkflowSkeletonCard key={`workflow-skeleton-card-${index}`} />;
                    })
                  : null}
              </Grid>
            </Box>
          )}

          {!!draftFlows.length && isOnline ? (
            <Box>
              <Typography color="text.secondary" mb={2}>
                Drafts
              </Typography>
              <Grid container rowSpacing={2} spacing={2}>
                {!isLoadingDraftFlows &&
                  draftFlows.map((flow) => (
                    <WorkflowDraftCard
                      key={flow.id}
                      flow={flow}
                      refetch={refetchDraftFlows}
                      setProcessing={setProcessing}
                    />
                  ))}
              </Grid>
            </Box>
          ) : null}
        </Stack>
      )}
    </PageLayout>
  );
};
